import { useState, useEffect, useRef, type FormEvent, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { callFn, waitlistHostList, type WaitlistTable, type WaitlistEntry } from "../../lib/api";

import { normalizeHostPhone, formatHostPhone, buildHostIntakePayload, hostIntakeError, parseHostLookupQuery, hostLookupError, type HostLookupQuery } from "../../lib/host-intake";
import { tableFitsParty, remainingTableCapacity, minimumGuestsPresent, hasMinimumGuestsPresent } from "../../lib/waitlist-workflow";
import { useConfirmDialog } from "../../components/ConfirmDialog";

/* ── Types ── */
interface CustomerContext {
  customer_id: string;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  visit_count: number;
  last_visit_at: string | null;
  sms_review_consent: boolean;
  guest_tier: "new" | "returning_recent" | "returning_dormant";
  days_since_last_visit: number | null;
  collective_33: boolean;
  email?: string | null;
  active_entry?: ActiveEntry | null;
}

interface ActiveEntry {
  id: string;
  status: string;
  first_name_at_intake: string | null;
  party_size: number;
  created_at: string;
}

interface LookupResult {
  phone: string | null;
  customers: CustomerContext[];
  active_entry: ActiveEntry | null;
  search_type?: "phone" | "name" | "email";
  truncated?: boolean;
}

type RoomPref = "any" | "lounge" | "library" | "parlor";

const ROOMS: Exclude<RoomPref, "any">[] = ["lounge", "parlor", "library"];
const ROOM_LABEL: Record<string, string> = {
  any:     "Any Room",
  lounge:  "Lounge",
  parlor:  "Parlor",
  library: "Library",
};
const ROOM_COLOR: Record<string, string> = {
  lounge:  "#7EB8F7",
  parlor:  "#D49BC4",
  library: "#7EC8A4",
};

function isRoomCompatible(room: string, intent: "neat" | "cocktails" | "food" | null): { compatible: boolean; reason?: string } {
  if (!intent) return { compatible: true };
  
  if (room === "library") {
    if (intent === "food") return { compatible: false, reason: "No Food Service" };
    if (intent === "cocktails") return { compatible: false, reason: "No Cocktails" };
  }
  
  if (room === "parlor") {
    if (intent === "food") return { compatible: false, reason: "No Food Service" };
  }
  
  return { compatible: true };
}

/* ── Guest tier badge ── */
function TierBadge({ tier }: { tier: CustomerContext["guest_tier"] }) {
  const label = tier === "new" ? "New" : tier === "returning_recent" ? "Returning" : "Dormant";
  const color = tier === "returning_recent" ? "var(--gold)" : tier === "new" ? "#4CAF50" : "var(--muted)";
  return (
    <span style={{ ...styles.badge, color, borderColor: color }}>
      {label}
    </span>
  );
}

export default function HostIntake() {
  const navigate = useNavigate();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const mountedRef = useRef(true);
  const intakeBusyRef = useRef(false);
  const lookupBusyRef = useRef(false);
  const lookupGenerationRef = useRef(0);
  const frozenNewGuestPayloadRef = useRef<ReturnType<typeof buildHostIntakePayload> | null>(null);

  /* ── Seating availability (loaded once on mount) ── */
  const [allTables, setAllTables]   = useState<WaitlistTable[]>([]);
  const [allEntries, setAllEntries] = useState<WaitlistEntry[]>([]);
  const [seatNowBusy, setSeatNowBusy] = useState<string | null>(null);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    function refresh() {
      waitlistHostList().then((data) => {
        if (!mountedRef.current || cancelled) return;
        setAllTables(data.tables);
        setAllEntries(data.entries);
        setAvailabilityError(null);
      }).catch(() => { if (mountedRef.current && !cancelled) setAvailabilityError("Table availability is stale — retrying."); });
    }
    refresh();
    // Poll the same shape as HostDashboard so "Seat now" doesn't double-book
    // a table seated minutes earlier from another device.
    const timer = setInterval(refresh, 5000);
    return () => {
      mountedRef.current = false;
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  /* ── Phase 1: phone lookup ── */
  const [lookupText, setLookupText] = useState("");
  const [phone, setPhone] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [activeProfile, setActiveProfile] = useState<CustomerContext | null>(null);

  /* ── Phase 2: customer selection ── */
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerContext | null>(null);
  const [customerChosen, setCustomerChosen] = useState(true);
  const [newGuest, setNewGuest] = useState(true);

  /* ── Phase 3: intake form ── */
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [partySize, setPartySize] = useState(2);
  const [menuIntent, setMenuIntent] = useState<"neat" | "cocktails" | "food" | null>(null);
  const [roomPref, setRoomPref] = useState<RoomPref>("any");
  const [parlorOk, setParlorOk] = useState(false);
  const [c33Priority, setC33Priority] = useState(false);
  const [celebNotes, setCelebNotes] = useState("");
  const [hostNotes, setHostNotes] = useState("");
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [guestsPresent, setGuestsPresent] = useState(1);
  const [savedAttendanceFailure, setSavedAttendanceFailure] = useState(false);
  const savedAttendanceFailureRef = useRef(false);
  const [reenteringLookup, setReenteringLookup] = useState(false);
  const [liveMatches, setLiveMatches] = useState<LookupResult | null>(null);
  const [liveLookupBusy, setLiveLookupBusy] = useState(false);
  const [liveLookupError, setLiveLookupError] = useState<string | null>(null);
  const liveLookupGeneration = useRef(0);

  useEffect(() => {
    const generation = ++liveLookupGeneration.current;
    const normalized = normalizeHostPhone(phone);
    const digits = phone.replace(/\D/g, "");
    const prefix = /^[\d\s().-]+$/.test(phone.trim()) && digits.length >= 4 && digits.length <= 9 ? digits : null;
    setLiveMatches(null);
    setLiveLookupError(null);
    if (!customerChosen || !newGuest || (!normalized && !prefix) || submitBusy || seatNowBusy || savedAttendanceFailure || frozenNewGuestPayloadRef.current) {
      setLiveLookupBusy(false);
      return;
    }
    setLiveLookupBusy(true);
    const timer = setTimeout(() => {
      callFn<LookupResult>("waitlist-customer-lookup", normalized ? { phone: normalized } : { phone_prefix: prefix }).then(result => {
        if (mountedRef.current && generation === liveLookupGeneration.current) setLiveMatches(result);
      }).catch(() => {
        if (mountedRef.current && generation === liveLookupGeneration.current) setLiveLookupError("Guest lookup failed. You can search again before adding the party.");
      }).finally(() => {
        if (mountedRef.current && generation === liveLookupGeneration.current) setLiveLookupBusy(false);
      });
    }, 300);
    return () => { clearTimeout(timer); liveLookupGeneration.current += 1; };
  }, [phone, customerChosen, newGuest, submitBusy, seatNowBusy, savedAttendanceFailure]);

  /* ── Handlers ── */
  async function doLookup(query: HostLookupQuery) {
    if (intakeBusyRef.current) return;
    lookupBusyRef.current = true;
    const generation = ++lookupGenerationRef.current;
    setLookupBusy(true);
    setLookupError(null);
    setLookupResult(null);
    setCustomerChosen(false);
    setSelectedCustomer(null);
    setNewGuest(false);
    setActiveProfile(null);
    frozenNewGuestPayloadRef.current = null;
    try {
      const result = await callFn<LookupResult>("waitlist-customer-lookup", query.kind === "phone" ? { phone: query.value } : { query: query.value });
      if (mountedRef.current && generation === lookupGenerationRef.current) setLookupResult(result);
    } catch (err: any) {
      if (mountedRef.current && generation === lookupGenerationRef.current) setLookupError(err.message || "Lookup failed.");
    } finally {
      if (generation === lookupGenerationRef.current) {
        lookupBusyRef.current = false;
        if (mountedRef.current) setLookupBusy(false);
      }
    }
  }

  async function onLookup(e: FormEvent) {
    e.preventDefault();
    const query = parseHostLookupQuery(lookupText);
    if (!query) { setLookupError(hostLookupError(lookupText)); return; }
    await doLookup(query);
  }

  function resetPartyDetails() {
    setPartySize(2);
    setGuestsPresent(1);
    setSavedAttendanceFailure(false);
    savedAttendanceFailureRef.current = false;
    setMenuIntent(null);
    setRoomPref("any");
    setParlorOk(false);
    setCelebNotes("");
    setHostNotes("");
    setSubmitError(null);
  }

  function onSelectCustomer(cust: CustomerContext | null, preserveDraft = false) {
    setLookupError(null);
    if (cust?.active_entry) {
      setActiveProfile(cust);
      return;
    }
    if (cust && !normalizeHostPhone(cust.phone ?? "")) {
      setLookupError("This guest profile has no usable US phone number. Add them as a new guest to seat immediately, or update their contact outside this intake.");
      return;
    }
    if (reenteringLookup && !cust) { backToNewGuest(); return; }
    if (!reenteringLookup && !preserveDraft) resetPartyDetails();
    else setLookupResult(null);
    setReenteringLookup(false);
    liveLookupGeneration.current += 1;
    setLiveMatches(null);
    setLiveLookupBusy(false);
    setSelectedCustomer(cust);
    setCustomerChosen(true);
    setNewGuest(cust === null);
    frozenNewGuestPayloadRef.current = null;
    setActiveProfile(null);
    if (cust) setPhone(normalizeHostPhone(cust.phone ?? "") ?? "");
    else setPhone(parseHostLookupQuery(lookupText)?.kind === "phone" ? normalizeHostPhone(lookupText) ?? "" : "");
    if (cust?.first_name) setFirstName(cust.first_name);
    else setFirstName("");
    if (cust?.last_name) setLastName(cust.last_name);
    else setLastName("");
    // Auto-flag C33 priority when they are an actual member
    setC33Priority(cust?.collective_33 === true);
  }

  function backToNewGuest() {
    lookupGenerationRef.current += 1;
    lookupBusyRef.current = false;
    setLookupBusy(false);
    setLookupResult(null);
    setActiveProfile(null);
    setLookupError(null);
    setReenteringLookup(false);
    setSelectedCustomer(null);
    setNewGuest(true);
    setCustomerChosen(true);
  }

  function findExistingGuest() {
    if (intakeBusyRef.current || savedAttendanceFailureRef.current || frozenNewGuestPayloadRef.current) return;
    lookupGenerationRef.current += 1;
    setLookupText(normalizeHostPhone(phone) ? formatHostPhone(phone) : [firstName, lastName].filter(Boolean).join(" ").trim());
    setLookupResult(null);
    setActiveProfile(null);
    setLookupError(null);
    setReenteringLookup(true);
    setCustomerChosen(false);
  }

  function startNewGuest() {
    if (intakeBusyRef.current) return;
    resetPartyDetails();
    setReenteringLookup(false);
    lookupGenerationRef.current += 1;
    lookupBusyRef.current = false;
    setLookupBusy(false);
    setLookupResult(null);
    setActiveProfile(null);
    setLookupError(null);
    setSelectedCustomer(null);
    setCustomerChosen(true);
    setNewGuest(true);
    setFirstName("");
    setLastName("");
    setPhone("");
    setLookupText("");
    setC33Priority(false);
    setSubmitError(null);
    frozenNewGuestPayloadRef.current = null;
  }

  // Derive move_to_room: when they prefer a specific room but parlor is OK as a temp seat
  function derivedMoveToRoom(): string | null {
    if (parlorOk && roomPref !== "any" && roomPref !== "parlor") return roomPref;
    return null;
  }

  function intakePayload(intakeMode?: "seat_now" | "waitlist") {
    if (newGuest && frozenNewGuestPayloadRef.current) return frozenNewGuestPayloadRef.current;
    const requestId = newGuest ? crypto.randomUUID() : undefined;
    const payload = buildHostIntakePayload({ phone, firstName, lastName, partySize, guestsPresent, roomPref,
      parlorOk, c33Priority, celebNotes, hostNotes, menuIntent,
      customerId: selectedCustomer?.customer_id ?? null,
      newGuest,
      intakeMode: newGuest ? intakeMode : undefined,
      intakeRequestId: requestId });
    if (newGuest) frozenNewGuestPayloadRef.current = payload;
    return payload;
  }

  function clearFrozenPayloadForDefiniteValidationError(error: unknown) {
    const apiError = error as { status?: number; saved?: boolean };
    if ((apiError?.status === 400 || apiError?.status === 422) && apiError.saved !== true) {
      frozenNewGuestPayloadRef.current = null;
    }
  }

  function resetNewGuestDraft() {
    setReenteringLookup(false);
    lookupGenerationRef.current += 1;
    lookupBusyRef.current = false;
    setLookupBusy(false);
    setCustomerChosen(true);
    setNewGuest(true);
    setSelectedCustomer(null);
    setLookupResult(null);
    setActiveProfile(null);
    setLookupError(null);
    setLookupText("");
    setPhone("");
    setFirstName("");
    setLastName("");
    resetPartyDetails();
    setC33Priority(false);
    setSubmitError(null);
    frozenNewGuestPayloadRef.current = null;
  }

  async function startAnotherParty() {
    if (newGuest && frozenNewGuestPayloadRef.current) {
      const confirmed = await confirm({
        title: "Start another party?",
        message: "The earlier party may already be saved. Check the dashboard before starting another party.",
        confirmLabel: "Start another party",
      });
      if (!confirmed) return;
    }
    resetNewGuestDraft();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (intakeBusyRef.current || savedAttendanceFailureRef.current || liveLookupBusy || !!liveMatches?.active_entry) return;
    intakeBusyRef.current = true;
    setSubmitBusy(true);
    setSubmitError(null);
    try {
      const payload = intakePayload("waitlist");
      const result = await callFn<{ entry_id: string; arrived_count?: number }>("waitlist-host-api", payload);
      if (result.arrived_count !== payload.arrived_count) {
        savedAttendanceFailureRef.current = true;
        if (mountedRef.current) {
          setSavedAttendanceFailure(true);
          setSubmitError("Party saved; attendance could not be confirmed. Check the dashboard before updating attendance.");
        }
        return;
      }
      navigate("/host/dashboard");
    } catch (err: unknown) {
      clearFrozenPayloadForDefiniteValidationError(err);
      if (mountedRef.current) setSubmitError(hostIntakeError(err));
    } finally {
      intakeBusyRef.current = false;
      if (mountedRef.current) setSubmitBusy(false);
    }
  }

  /* ── Available tables ── */
  const availableTables = allTables.filter(
    (t) => tableFitsParty(t, allEntries, partySize, menuIntent ? `[Intent: ${menuIntent === "food" ? "Food & Cocktails" : menuIntent === "cocktails" ? "Cocktails Only" : "Neat Pours"}]` : null)
  );
  // Group by room — always index all 3 rooms so columns render even if empty
  const availableByRoom = ROOMS.reduce<Record<string, WaitlistTable[]>>((acc, room) => {
    acc[room] = availableTables.filter((t) => t.room_id === room);
    return acc;
  }, {} as Record<string, WaitlistTable[]>);

  async function handleSeatNow(tableId: string) {
    if (intakeBusyRef.current || savedAttendanceFailureRef.current || liveLookupBusy || !!liveMatches?.active_entry || !hasMinimumGuestsPresent(partySize, guestsPresent)) return;
    intakeBusyRef.current = true;
    const confirmed = await confirm({ title: "Confirm guests present", message: `Confirm ${guestsPresent} of ${partySize} guests are present and ready to be seated.`, confirmLabel: "Confirm and seat" });
    if (!confirmed) { intakeBusyRef.current = false; return; }
    setSeatNowBusy(tableId);
    setSubmitError(null);
    let entryId: string | null = null;
    try {
      const payload = intakePayload("seat_now");
      const r = await callFn<{ ok: true; entry_id: string; sms: unknown; status?: string; arrived_count?: number }>(
        "waitlist-host-api", payload
      );
      entryId = r.entry_id;
      if (r.status === "seated") {
        navigate("/host/dashboard");
        return;
      }
      if (["departed", "no_show", "departed_unseated", "terminal"].includes(r.status ?? "")) {
        setSubmitError("This saved party is already closed. Start another party to create a new entry.");
        return;
      }
      if (r.arrived_count !== payload.arrived_count) {
        savedAttendanceFailureRef.current = true;
        if (mountedRef.current) {
          setSavedAttendanceFailure(true);
          setSubmitError("Party saved; attendance could not be confirmed. Check the dashboard before seating.");
        }
        return;
      }
      await callFn("waitlist-host-api", { action: "seat", entry_id: entryId, table_id: tableId });
      navigate("/host/dashboard");
    } catch (err: any) {
      if (mountedRef.current) {
        // If add_entry succeeded but seat failed, the guest is silently on the
        // waitlist — flag this explicitly so the host doesn't keep retrying
        // (which then errors with active_entry_exists).
        const msg = hostIntakeError(err);
        setSubmitError(
          entryId
            ? `${msg} — guest was added to the waitlist; pick them up from the dashboard to seat.`
            : msg
        );
      }
      if (!entryId) clearFrozenPayloadForDefiniteValidationError(err);
    } finally {
      intakeBusyRef.current = false;
      if (mountedRef.current) setSeatNowBusy(null);
    }
  }

  /* ── Render ── */
  const showSeatingSection = allTables.length > 0;
  const anyAvailable = availableTables.length > 0;
  const namesValid = !newGuest || firstName.trim() !== "";
  const waitlistPhoneValid = !!normalizeHostPhone(phone);
  const guestDetailsLocked = !newGuest || submitBusy || !!seatNowBusy || !!frozenNewGuestPayloadRef.current;
  const intakeControlsLocked = submitBusy || !!seatNowBusy || savedAttendanceFailure || !!frozenNewGuestPayloadRef.current;

  return (
    <div style={styles.shell}>
      <h2 style={styles.heading}>Add Party</h2>

      {/* ── Guest lookup form ── */}
      {!customerChosen && (<form onSubmit={onLookup} style={styles.row}>
        <input
          aria-label="Guest phone, name or email"
          inputMode="text"
          maxLength={254}
          placeholder="(816) 555-0100"
          value={lookupText}
          onChange={(e) => {
            const raw = e.target.value;
            const display = normalizeHostPhone(raw) ? formatHostPhone(raw) : raw;
            setLookupText(display);
            lookupGenerationRef.current += 1;
            lookupBusyRef.current = false;
            setLookupBusy(false);
            setLookupError(null);
            setLookupResult(null);
            setActiveProfile(null);
            setCustomerChosen(false);
            setSelectedCustomer(null);
            const query = parseHostLookupQuery(raw);
            if (query?.kind === "phone") void doLookup(query);
          }}
          style={styles.phoneInput}
          disabled={submitBusy || !!seatNowBusy}
        />
        <div style={styles.lookupActions}>
          <button
            type="submit"
            style={styles.lookupBtn}
            disabled={lookupBusy || submitBusy || !!seatNowBusy || !parseHostLookupQuery(lookupText)}
          >
            {lookupBusy ? "Looking up…" : "Look up"}
          </button>
          {reenteringLookup ? <button type="button" onClick={backToNewGuest} style={styles.newGuestShortcut}>Back to New Guest</button> : <button type="button" onClick={startNewGuest} style={styles.newGuestShortcut}>
            New Guest
          </button>}
        </div>
      </form>)}

      {!customerChosen && <p style={styles.inlineHelp}>Phone is fastest and looks up automatically after 10 digits. Or enter a name (at least 2 letters or numbers) or a full email, then select Look up.</p>}

      {lookupError && <p style={styles.error}>{lookupError}</p>}
      {availabilityError && <p role="alert" style={styles.error}>{availabilityError}</p>}
      {confirmDialog}

      {/* ── Active entry guard ── */}
      {lookupResult?.active_entry && !activeProfile && (
        <div style={styles.alertBox}>
          <strong>Already on the list</strong>
          <p style={{ margin: "4px 0 0" }}>
            {lookupResult.active_entry.first_name_at_intake
              ? `${lookupResult.active_entry.first_name_at_intake} — `
              : ""}
            party of {lookupResult.active_entry.party_size}, status:{" "}
            <strong>{lookupResult.active_entry.status}</strong>.
          </p>
          <button type="button" onClick={() => navigate("/host/dashboard")} style={styles.dashLink}>
            Go to dashboard →
          </button>
        </div>
      )}

      {activeProfile?.active_entry && (
        <div style={styles.alertBox}>
          <strong>Already on the list</strong>
          <p style={{ margin: "4px 0 0" }}>
            {activeProfile.active_entry.first_name_at_intake ? `${activeProfile.active_entry.first_name_at_intake} — ` : ""}
            party of {activeProfile.active_entry.party_size}, status: <strong>{activeProfile.active_entry.status}</strong>.
          </p>
          <button type="button" onClick={() => setActiveProfile(null)} style={styles.dashLink}>← Back to matches</button>
          <button type="button" onClick={() => navigate("/host/dashboard")} style={{ ...styles.dashLink, marginLeft: 16 }}>Go to dashboard →</button>
        </div>
      )}

      {/* ── Customer selection ── */}
      {lookupResult && !lookupResult.active_entry && !activeProfile && !customerChosen && (
        <div style={styles.section}>
          <p style={styles.sectionLabel}>
            {lookupResult.customers.length > 0
              ? "Returning guest — confirm identity:"
              : "No match — new guest."}
          </p>
          {lookupResult.truncated && <p style={styles.inlineHelp}>Showing the first 10 matches. Refine your search to narrow the list.</p>}
          {lookupResult.customers.map((c) => {
            const usablePhone = !!normalizeHostPhone(c.phone ?? "");
            return <div key={c.customer_id}>
            <button type="button" onClick={() => onSelectCustomer(c)}
              style={{ ...styles.customerCard, ...(!usablePhone ? { opacity: 0.65 } : {}) }}>
              <span style={styles.customerName}>{c.first_name} {c.last_name}</span>
              <TierBadge tier={c.guest_tier} />
              {c.collective_33 && (
                <span style={styles.c33Chip}>C33</span>
              )}
              <span style={{ ...styles.customerSub, overflowWrap: "anywhere" }}>
                {lookupResult.search_type !== "phone" && <>{c.phone ? formatHostPhone(c.phone) : "No phone"}{c.email ? ` · ${c.email}` : ""}<br /></>}
                {c.visit_count} visit{c.visit_count !== 1 ? "s" : ""}
                {c.days_since_last_visit != null
                  ? ` · last ${Math.round(c.days_since_last_visit)}d ago`
                  : ""}
              </span>
            </button>
            {!usablePhone && <p style={styles.inlineHelp}>This profile cannot be selected because a usable US phone number is required for a returning guest.</p>}
            </div>;
          })}
          <button type="button" onClick={() => onSelectCustomer(null)} style={styles.newGuestBtn}>
            {reenteringLookup ? "Back to New Guest" : lookupResult.customers.length > 0 ? "Not them — add as new guest" : "Add as new guest"}
          </button>
        </div>
      )}

      {/* ── Intake form ── */}
      {customerChosen && !lookupResult?.active_entry && (
        <form onSubmit={onSubmit} style={styles.form}>
          {selectedCustomer && (
            <div style={styles.customerBanner}>
              <strong>{selectedCustomer.first_name} {selectedCustomer.last_name}</strong>
              <TierBadge tier={selectedCustomer.guest_tier} />
              {selectedCustomer.collective_33 && (
                <span style={styles.c33Chip}>C33</span>
              )}
            </div>
          )}

          {newGuest && <button type="button" onClick={findExistingGuest} disabled={intakeControlsLocked} style={styles.newGuestBtn}>Find existing guest</button>}

          {/* Party Size & Name(s) Row */}
          <div style={styles.gridRow}>
            {/* Party size */}
            <div style={{ flex: "0 0 160px", display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={styles.intentLabel}>Party size</span>
              <div style={styles.stepperRow}>
                <button type="button" onClick={() => { setPartySize((n) => Math.max(1, n - 1)); setGuestsPresent(1); }}
                  style={styles.stepBtn} aria-label="Decrease party size" disabled={intakeControlsLocked}>−</button>
                <span style={styles.stepValue}>{partySize}</span>
                <button type="button" onClick={() => { setPartySize((n) => Math.min(30, n + 1)); setGuestsPresent(1); }}
                  style={styles.stepBtn} aria-label="Increase party size" disabled={intakeControlsLocked}>+</button>
              </div>
            </div>

            {/* Name fields */}
            <div style={{ flex: 1, display: "flex", gap: 10, flexWrap: "wrap" as const }}>
              <label style={{ ...styles.label, flex: "1 1 140px" }}>
                First Name *
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First" style={styles.input} autoFocus={newGuest} required={newGuest}
                  readOnly={!newGuest} disabled={guestDetailsLocked} />
              </label>
              <label style={{ ...styles.label, flex: "1 1 140px" }}>
                Last Name{newGuest ? " (optional)" : ""}
                <input value={lastName} onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last" style={styles.input} readOnly={!newGuest} disabled={guestDetailsLocked} />
              </label>
            </div>
          </div>

          <label style={styles.label}>
            Guests present
            <select aria-label="Guests present" value={guestsPresent} disabled={intakeControlsLocked} onChange={e => setGuestsPresent(Number(e.target.value))} style={styles.input}>
              {Array.from({ length: partySize }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1} of {partySize}</option>)}
            </select>
            <span style={styles.inlineHelp}>At least {minimumGuestsPresent(partySize)} of {partySize} guests must be present to seat. The entire party is expected.</span>
          </label>

          {newGuest && (
            <>
            <label style={styles.label}>
              Phone (optional when seating now)
              <input
                aria-label="New guest phone number"
                inputMode="numeric"
                value={phone}
                onChange={(e) => { liveLookupGeneration.current += 1; setLiveMatches(null); setLiveLookupError(null); setPhone(formatHostPhone(e.target.value)); }}
                placeholder="(816) 555-0100"
                style={styles.input}
                disabled={guestDetailsLocked}
              />
              <span style={styles.inlineHelp}>A phone number is required to add this party to the waitlist. Seat now can continue without one.</span>
            </label>
            <div aria-label="Matching guests" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {liveLookupBusy && <span style={styles.inlineHelp}>Looking for existing guests…</span>}
              {liveLookupError && <span style={styles.error}>{liveLookupError}</span>}
              {liveMatches?.active_entry && <span style={styles.error}>This phone is already on the waitlist. Check the dashboard before adding another party.</span>}
              {liveMatches && !liveMatches.active_entry && liveMatches.customers.map(c => <button key={c.customer_id} type="button" disabled={intakeControlsLocked} onClick={() => onSelectCustomer(c, true)} style={styles.customerCard}><span style={styles.customerName}>{c.first_name} {c.last_name}</span><span style={styles.customerSub}>{c.phone ? formatHostPhone(c.phone) : "No phone"}</span></button>)}
            </div>
            </>
          )}

          {/* Guest Menu Focus Intent Selection */}
          <div style={styles.intentContainer}>
            <span style={styles.intentLabel}>What is the guest's menu focus?</span>
            <div style={styles.intentOptions}>
              <button
                type="button"
                disabled={intakeControlsLocked}
                onClick={() => {
                  setMenuIntent("food");
                  setRoomPref("lounge");
                  setParlorOk(false);
                }}
                style={{
                  ...styles.intentBtn,
                  ...(menuIntent === "food" ? styles.intentBtnActive : {}),
                }}
              >
                <span style={styles.intentEmoji}>🍽️</span>
                <span style={styles.intentTitle}>Cocktails & Food</span>
                <span style={styles.intentSlogan}>Full dinner & drinks menu (Lounge)</span>
              </button>

              <button
                type="button"
                disabled={intakeControlsLocked}
                onClick={() => {
                  setMenuIntent("cocktails");
                  setRoomPref("parlor");
                  setParlorOk(false);
                }}
                style={{
                  ...styles.intentBtn,
                  ...(menuIntent === "cocktails" ? styles.intentBtnActive : {}),
                }}
              >
                <span style={styles.intentEmoji}>🍸</span>
                <span style={styles.intentTitle}>Cocktails Only</span>
                <span style={styles.intentSlogan}>Craft cocktails list (Parlor)</span>
              </button>

              <button
                type="button"
                disabled={intakeControlsLocked}
                onClick={() => {
                  setMenuIntent("neat");
                  setRoomPref("library");
                  setParlorOk(true); // Auto-allow Parlor while waiting for Library neat pours!
                }}
                style={{
                  ...styles.intentBtn,
                  ...(menuIntent === "neat" ? styles.intentBtnActive : {}),
                }}
              >
                <span style={styles.intentEmoji}>🥃</span>
                <span style={styles.intentTitle}>Neat Pours Only</span>
                <span style={styles.intentSlogan}>Whiskey flights & tasting (Library)</span>
              </button>
            </div>
          </div>

          {/* ── 3-column seating / room section ── */}
          <div style={styles.roomSection}>
            <div style={styles.roomSectionHeader}>
              <p style={styles.roomSectionTitle}>
                {showSeatingSection && anyAvailable
                  ? "Open seating — seat now or add to waitlist"
                  : showSeatingSection
                    ? "No open seating for party of " + partySize + " — add to waitlist"
                    : "Room preference"}
              </p>
            </div>

            {/* 3 columns: Lounge | Parlor | Library */}
            <div style={styles.roomColumns}>
              {ROOMS.map((room) => {
                const roomTables = availableByRoom[room] ?? [];
                const isSelected = roomPref === room;
                const rc = ROOM_COLOR[room];
                const { compatible, reason } = isRoomCompatible(room, menuIntent);
                return (
                  <div key={room} style={{
                    ...styles.roomCol,
                    ...(isSelected ? { background: `${rc}08` } : {}),
                    ...(!compatible ? { opacity: 0.45 } : {}),
                  }}>
                    {/* Column header = room preference selector */}
                    <button
                      type="button"
                      disabled={!compatible || intakeControlsLocked}
                      onClick={() => setRoomPref(isSelected ? "any" : room)}
                      style={{
                        ...styles.roomColBtn,
                        ...(isSelected ? {
                          color: rc,
                          background: `${rc}14`,
                          borderBottom: `1px solid ${rc}40`,
                        } : {}),
                      }}
                    >
                      {ROOM_LABEL[room]}
                      {isSelected && <span style={{ ...styles.roomColCheck, color: rc }}>✓</span>}
                    </button>

                    {/* Available tables for this room */}
                    <div style={styles.roomColTables}>
                      {!showSeatingSection ? null : !compatible ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, padding: "16px 6px", textAlign: "center" }}>
                          <span style={{ fontSize: 16 }}>🚫</span>
                          <span style={{ fontSize: 9, fontWeight: 800, color: "#FF8A7A", textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: "1.3" }}>
                            {reason}
                          </span>
                        </div>
                      ) : roomTables.length === 0 ? (
                        <span style={styles.roomColNone}>—</span>
                      ) : (
                        roomTables.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            disabled={!!seatNowBusy || submitBusy || savedAttendanceFailure || liveLookupBusy || !!liveMatches?.active_entry || !namesValid || !hasMinimumGuestsPresent(partySize, guestsPresent)}
                            onClick={() => handleSeatNow(t.id)}
                            style={{ ...styles.seatNowChip, border: `1px solid ${rc}`, background: `${rc}14`, color: rc }}
                          >
                            <span>{seatNowBusy === t.id ? "Seating…" : t.label}</span>
                            <span style={styles.seatNowCap}>{remainingTableCapacity(t, allEntries)} free</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Start in Parlor Guest Service Question */}
            {showSeatingSection && (roomPref === "library" || roomPref === "lounge") && (
              <div style={styles.parlorOkFooter}>
                <span style={styles.parlorOkLabel}>
                  If {ROOM_LABEL[roomPref]} is full, do they want to start in the Parlor?
                </span>
                <div style={styles.parlorOkBtns}>
                  <button
                    type="button"
                    disabled={intakeControlsLocked}
                    onClick={() => setParlorOk(true)}
                    style={{
                      ...styles.parlorOkBtn,
                      ...(parlorOk ? styles.parlorOkBtnActive : {}),
                    }}
                  >
                    Yes, start in Parlor
                  </button>
                  <button
                    type="button"
                    disabled={intakeControlsLocked}
                    onClick={() => setParlorOk(false)}
                    style={{
                      ...styles.parlorOkBtn,
                      ...(!parlorOk ? styles.parlorOkBtnActive : {}),
                    }}
                  >
                    No, wait for {ROOM_LABEL[roomPref]}
                  </button>
                </div>
              </div>
            )}

            {/* Derived move-to-room transfer hint */}
            {derivedMoveToRoom() && (
              <div style={styles.roomFooter}>
                <span style={styles.moveHint}>
                  → Will be flagged for {ROOM_LABEL[derivedMoveToRoom()!]} transfer if seated in Parlor
                </span>
              </div>
            )}

            {showSeatingSection && anyAvailable && (
              <p style={styles.orLabel}>— or add to waitlist below —</p>
            )}
          </div>

          {/* C33 priority */}
          <label style={styles.toggle}>
            <input type="checkbox" checked={c33Priority}
              onChange={(e) => setC33Priority(e.target.checked)} disabled />
            C33 member priority (verified from the guest profile)
          </label>

          {/* Celebration notes */}
          <label style={styles.label}>
            Celebration notes
            <input value={celebNotes} onChange={(e) => setCelebNotes(e.target.value)}
              placeholder="Birthday, anniversary…" style={styles.input} disabled={intakeControlsLocked} />
          </label>

          {/* Host notes */}
          <label style={styles.label}>
            Host notes
            <input value={hostNotes} onChange={(e) => setHostNotes(e.target.value)}
              placeholder="Accessibility, regulars, flags…" style={styles.input} disabled={intakeControlsLocked} />
          </label>

          {submitError && <p style={styles.error}>{submitError}</p>}

          <div style={styles.formFooter}>
            <button type="button"
              onClick={() => { void (newGuest ? startAnotherParty() : (setCustomerChosen(false), setSelectedCustomer(null))); }}
              style={styles.backBtn} disabled={submitBusy || !!seatNowBusy}>
              {newGuest ? "Start another party" : "← Back"}
            </button>
            <button type="submit" style={styles.submitBtn} disabled={submitBusy || !!seatNowBusy || savedAttendanceFailure || liveLookupBusy || !!liveMatches?.active_entry || !namesValid || !waitlistPhoneValid}>
              {submitBusy ? "Adding…" : !namesValid ? "Enter first name" : !waitlistPhoneValid ? "Phone required for waitlist" : "Add to waitlist"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  shell: {
    width: "90%",
    margin: "0 auto",
    padding: "8px 0 48px",
  },
  gridRow: {
    display: "flex",
    gap: 20,
    alignItems: "flex-start",
    flexWrap: "wrap",
    marginBottom: 10,
    width: "100%",
  },
  heading: {
    fontFamily: "Georgia, serif",
    fontSize: 22,
    color: "var(--text)",
    margin: "0 0 20px",
  },
  row: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  lookupActions: {
    display: "flex",
    gap: 8,
    flexShrink: 0,
  },
  nameRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },

  /* ── Room section ── */
  roomSection: {
    border: "1px solid var(--border)",
    borderRadius: 12,
    overflow: "hidden",
  },
  roomSectionHeader: {
    padding: "10px 14px",
    borderBottom: "1px solid var(--border)",
    background: "var(--surface)",
  },
  roomSectionTitle: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase" as const,
    letterSpacing: 0.6,
    color: "var(--muted)",
  },
  roomColumns: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
  },
  roomCol: {
    borderRight: "1px solid var(--border)",
    display: "flex",
    flexDirection: "column" as const,
    gap: 0,
    background: "var(--bg)",
  },
  roomColActive: {
    background: "rgba(249,175,54,0.04)",
  },
  roomColBtn: {
    width: "100%",
    padding: "10px 12px",
    background: "transparent",
    border: 0,
    borderBottom: "1px solid var(--border)",
    color: "var(--muted)",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  roomColBtnActive: {
    color: "var(--gold)",
    background: "rgba(249,175,54,0.08)",
  },
  roomColCheck: {
    fontSize: 11,
    color: "var(--gold)",
  },
  roomColTables: {
    padding: "10px 10px",
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
    minHeight: 48,
  },
  roomColNone: {
    color: "var(--border)",
    fontSize: 18,
    fontWeight: 700,
    textAlign: "center" as const,
    padding: "4px 0",
    display: "block",
    width: "100%",
  },
  seatNowChip: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #4CAF50",
    background: "rgba(76,175,80,0.1)",
    color: "#4CAF50",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    gap: 2,
    width: "100%",
  },
  seatNowCap: {
    fontSize: 9,
    fontWeight: 600,
    color: "var(--muted)",
  },
  parlorOkToggle: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 10px",
    fontSize: 11,
    fontWeight: 600,
    color: "var(--muted)",
    cursor: "pointer",
    borderTop: "1px solid var(--border)",
  },
  roomFooter: {
    borderTop: "1px solid var(--border)",
    padding: "10px 14px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap" as const,
    background: "var(--surface)",
  },
  anyBtn: {
    padding: "6px 14px",
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: "transparent",
    color: "var(--muted)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  anyBtnActive: {
    border: "1px solid var(--gold)",
    color: "var(--gold)",
    background: "rgba(249,175,54,0.08)",
  },
  moveHint: {
    fontSize: 11,
    color: "#FF9800",
    fontStyle: "italic",
    flex: 1,
  },
  orLabel: {
    margin: 0,
    textAlign: "center" as const,
    fontSize: 11,
    color: "var(--muted)",
    fontStyle: "italic",
    padding: "8px 0",
    borderTop: "1px solid var(--border)",
    background: "var(--surface)",
  },

  /* ── Rest of form ── */
  phoneInput: {
    flex: "1 1 220px",
    minWidth: 0,
    height: 44,
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    padding: "0 14px",
    fontSize: 18,
    outline: "none",
    letterSpacing: "0.02em",
  },
  lookupBtn: {
    height: 44,
    padding: "0 18px",
    borderRadius: 12,
    border: "1px solid var(--gold)",
    background: "var(--gold)",
    color: "#0D0B08",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  newGuestShortcut: {
    minHeight: 44,
    padding: "0 18px",
    borderRadius: 12,
    border: "2px solid var(--gold)",
    background: "rgba(249,175,54,0.1)",
    color: "var(--gold)",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  error: {
    margin: "0 0 12px",
    padding: 10,
    background: "rgba(224, 80, 64, 0.12)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    borderRadius: 10,
    color: "#FF8A7A",
    fontSize: 13,
    fontWeight: 600,
  },
  alertBox: {
    padding: 14,
    border: "1px solid rgba(249, 175, 54, 0.4)",
    borderRadius: 12,
    background: "rgba(249, 175, 54, 0.08)",
    marginBottom: 16,
  },
  dashLink: {
    marginTop: 10,
    display: "inline-block",
    background: "transparent",
    color: "var(--gold)",
    border: 0,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    padding: 0,
  },
  section: {
    marginBottom: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  sectionLabel: {
    margin: "0 0 4px",
    fontSize: 12,
    color: "var(--muted)",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  customerCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    cursor: "pointer",
    textAlign: "left",
    flexWrap: "wrap",
  },
  customerName: {
    fontSize: 15,
    fontWeight: 700,
    color: "var(--text)",
    flex: 1,
  },
  customerSub: {
    fontSize: 11,
    color: "var(--muted)",
    width: "100%",
  },
  badge: {
    fontSize: 10,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    padding: "2px 7px",
    borderRadius: 6,
    border: "1px solid",
  },
  c33Chip: {
    fontSize: 10,
    fontWeight: 800,
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    padding: "2px 7px",
    borderRadius: 6,
    border: "1px solid rgba(249,175,54,0.5)",
    color: "var(--gold)",
    background: "rgba(249,175,54,0.1)",
  },
  newGuestBtn: {
    display: "block",
    width: "100%",
    padding: "11px 14px",
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "transparent",
    color: "var(--muted)",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "center",
  },
  customerBanner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    marginBottom: 4,
    fontSize: 14,
    fontWeight: 700,
    color: "var(--text)",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  label: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "var(--muted)",
    fontWeight: 800,
  },
  input: {
    height: 44,
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    padding: "0 14px",
    fontSize: 15,
    outline: "none",
    fontFamily: "inherit",
  },
  inlineHelp: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0,
    textTransform: "none" as const,
    color: "var(--muted)",
  },
  stepperRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginTop: 2,
  },
  stepBtn: {
    width: 44,
    height: 44,
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    fontSize: 22,
    fontWeight: 400,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  stepValue: {
    fontSize: 24,
    fontWeight: 800,
    color: "var(--text)",
    minWidth: 32,
    textAlign: "center",
  },
  toggle: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text)",
    cursor: "pointer",
  },
  formFooter: {
    display: "flex",
    gap: 10,
    marginTop: 8,
  },
  backBtn: {
    flex: "0 0 auto",
    height: 44,
    padding: "0 18px",
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "transparent",
    color: "var(--muted)",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  submitBtn: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    border: "1px solid var(--gold)",
    background: "var(--gold)",
    color: "#0D0B08",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
  },
  intentContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 10,
    marginBottom: 10,
  },
  intentLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "var(--muted)",
    fontWeight: 800,
  },
  intentOptions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap" as const,
    width: "100%",
  },
  intentBtn: {
    flex: "1 1 180px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "16px 12px",
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--surface, #15110E)",
    cursor: "pointer",
    outline: "none",
    transition: "all 0.15s ease",
    textAlign: "center" as const,
  },
  intentBtnActive: {
    background: "rgba(249, 175, 54, 0.08)",
    border: "1px solid var(--gold)",
  },
  intentEmoji: {
    fontSize: 22,
    marginBottom: 6,
  },
  intentTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text)",
    marginBottom: 4,
  },
  intentSlogan: {
    fontSize: 10,
    color: "var(--muted)",
    lineHeight: "1.2",
  },
  parlorOkFooter: {
    borderTop: "1px solid var(--border)",
    padding: "16px 14px",
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
    background: "var(--surface)",
  },
  parlorOkLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "var(--text)",
  },
  parlorOkBtns: {
    display: "flex",
    gap: 10,
  },
  parlorOkBtn: {
    flex: 1,
    height: 38,
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: "var(--bg)",
    color: "var(--muted)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    outline: "none",
    transition: "all 0.15s ease",
  },
  parlorOkBtnActive: {
    border: "1px solid var(--gold)",
    background: "rgba(249, 175, 54, 0.08)",
    color: "var(--gold)",
  },
};
